import { FC, useMemo, useState } from 'react';
import { Button, Spinner } from '../../';
import { StewardReportDetail } from '../../../services/dtos';
import { calculateReportSummary, calculateReportSummaryFromLocalState, formatCurrency, formatHours } from '../../../lib/helpers/stewardReportCalculations';
import stewardReportServices from '../../../services/stewardReportServices';

interface ReportSummaryPanelProps {
  details: StewardReportDetail[];
  isReadOnly?: boolean;
  workerLocalStates?: Map<string, { localValues: Map<string, string>; rates: { regTimeRate: number; showCallRate: number; mealPenaltyRate: number } }>;
  weekDates?: Date[];
  headerId?: number;
  hasUnsavedChanges?: boolean;
  onFinalize?: (headerId: number) => Promise<void>;
}

export const ReportSummaryPanel: FC<ReportSummaryPanelProps> = ({
  details,
  isReadOnly = false,
  workerLocalStates,
  weekDates,
  headerId,
  hasUnsavedChanges = false,
  onFinalize,
}) => {
  const [finalizing, setFinalizing] = useState(false);

  const reportSummary = useMemo(() => {
    // Use local state if available (reflects unsaved changes), otherwise use API data
    if (workerLocalStates && workerLocalStates.size > 0 && weekDates) {
      return calculateReportSummaryFromLocalState(workerLocalStates, weekDates, details);
    }
    return calculateReportSummary(details);
  }, [details, workerLocalStates, weekDates]);

  const handleFinalize = async () => {
    if (!headerId || !onFinalize || hasUnsavedChanges || finalizing) return;

    setFinalizing(true);
    try {
      await onFinalize(headerId);
    } catch (err) {
      console.error('Error finalizing report:', err);
      // Error handling is done in parent component
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold dark:text-white">Report Summary</h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {reportSummary.workerCount} {reportSummary.workerCount === 1 ? 'worker' : 'workers'}
          </span>
        </div>
      </div>

      {/* Content - Scrollable */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Hours Totals */}
        <div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div className="flex flex-col">
              <span className="text-gray-600 dark:text-gray-400 text-xs">Reg Hrs</span>
              <span className="font-medium dark:text-white">{formatHours(reportSummary.totalRegHours)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-600 dark:text-gray-400 text-xs">OT Hrs</span>
              <span className="font-medium dark:text-white">{formatHours(reportSummary.totalOtHours)}</span>
            </div>
            <div className="flex flex-col">
              <span className="text-gray-600 dark:text-gray-400 text-xs">DT Hrs</span>
              <span className="font-medium dark:text-white">{formatHours(reportSummary.totalDtHours)}</span>
            </div>
          </div>
        </div>

        {/* Quantities */}
        {(reportSummary.totalShowCallQty > 0 || reportSummary.totalMealPenaltyQty > 0) && (
          <div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex flex-col">
                <span className="text-gray-600 dark:text-gray-400 text-xs">Show Calls</span>
                <span className="font-medium dark:text-white">{reportSummary.totalShowCallQty}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-gray-600 dark:text-gray-400 text-xs">Meal Penalties</span>
                <span className="font-medium dark:text-white">{reportSummary.totalMealPenaltyQty}</span>
              </div>
            </div>
          </div>
        )}

        {/* Wage Breakdown */}
        <div>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">Regular Wage:</span>
              <span className="font-medium dark:text-white">{formatCurrency(reportSummary.totalRegWage)}</span>
            </div>
            {reportSummary.totalOtWage > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">Overtime Wage:</span>
                <span className="font-medium dark:text-white">{formatCurrency(reportSummary.totalOtWage)}</span>
              </div>
            )}
            {reportSummary.totalDtWage > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">Double Time Wage:</span>
                <span className="font-medium dark:text-white">{formatCurrency(reportSummary.totalDtWage)}</span>
              </div>
            )}
            {reportSummary.totalShowCallWage > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">Show Call Wage:</span>
                <span className="font-medium dark:text-white">{formatCurrency(reportSummary.totalShowCallWage)}</span>
              </div>
            )}
            {reportSummary.totalMealPenaltyWage > 0 && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">Meal Penalty Wage:</span>
                <span className="font-medium dark:text-white">{formatCurrency(reportSummary.totalMealPenaltyWage)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs font-medium border-t border-gray-200 dark:border-gray-700 pt-1.5 mt-1.5">
              <span className="dark:text-white">Subtotal:</span>
              <span className="dark:text-white">{formatCurrency(reportSummary.totalSubtotal)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">Vacation (8%):</span>
              <span className="font-medium dark:text-white">{formatCurrency(reportSummary.totalVacation)}</span>
            </div>
            <div className="flex justify-between text-xs font-semibold border-t border-gray-200 dark:border-gray-700 pt-1.5 mt-1.5">
              <span className="dark:text-white">Total Wage:</span>
              <span className="dark:text-white">{formatCurrency(reportSummary.totalWage)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">Union Fee (3.5%):</span>
              <span className="font-medium dark:text-white">{formatCurrency(reportSummary.totalUnionFee)}</span>
            </div>
          </div>
        </div>

        {/* Report-Level Add-Ons */}
        <div>
          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5 uppercase tracking-wide">
            Add-Ons
          </h4>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">Gross Wage Total:</span>
              <span className="font-medium dark:text-white">{formatCurrency(reportSummary.grossWageTotal)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">Health & Welfare (15.5%):</span>
              <span className="font-medium dark:text-white">{formatCurrency(reportSummary.healthAndWelfare)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">Pension (10.4%):</span>
              <span className="font-medium dark:text-white">{formatCurrency(reportSummary.pension)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">Work Fee (3.5%):</span>
              <span className="font-medium dark:text-white">{formatCurrency(reportSummary.workFee)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600 dark:text-gray-400">Training (0.5%):</span>
              <span className="font-medium dark:text-white">{formatCurrency(reportSummary.training)}</span>
            </div>
            <div className="flex justify-between text-xs font-semibold border-t border-gray-200 dark:border-gray-700 pt-1.5 mt-1.5">
              <span className="dark:text-white">Total Add-Ons:</span>
              <span className="dark:text-white">{formatCurrency(reportSummary.totalAddOns)}</span>
            </div>
          </div>
        </div>

        {/* Grand Total */}
        <div className="border-t-2 border-gray-300 dark:border-gray-600 pt-3">
          <div className="flex justify-between text-base font-bold">
            <span className="dark:text-white">Grand Total:</span>
            <span className="dark:text-white">{formatCurrency(reportSummary.grandTotal)}</span>
          </div>
        </div>
      </div>

      {/* Finalize Button */}
      {!isReadOnly && headerId && (
        <div className="p-3 border-t border-gray-200 dark:border-gray-700">
          <Button
            color="success"
            size="lg"
            className="w-full"
            onClick={handleFinalize}
            disabled={hasUnsavedChanges || finalizing || !headerId}
          >
            {finalizing ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Finalizing...
              </>
            ) : (
              'Finalize Report'
            )}
          </Button>
          {hasUnsavedChanges && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
              Please save all changes before finalizing
            </p>
          )}
        </div>
      )}
    </div>
  );
};

